import { toast } from '@cencosud-cencommerce/paris-uikit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';

import type { RootState } from '@/Infrastructure/State';
import { setGiftCardError } from '@/modules/perseo-billing/store/slices/billing';
import { getGiftCardCanPayTotalAmount } from '@/modules/perseo-billing/utils';
import type {
  IErrorHandlerBilling,
  IErrorResponse
} from '@/modules/perseo-billing/utils/error-handler';
import { errorHandlerBilling } from '@/modules/perseo-billing/utils/error-handler';

import type { Cart, GiftCardFormFields } from '../../domain/Cart';
import {
  deleteGiftCard,
  postValidateGiftCard
} from '../../services/api/mutation';
import { httpCartDtoToCart } from '../mappers/httpCartDtoToCart';

const ADD_GIFTCARD = 'cart/addGiftcard';
const REMOVE_GIFTCARD = 'cart/removeGiftCard';

const getGiftCardPayent = (cart: Cart) => {
  const payments = cart?.payments || [];
  return payments.find(
    (payment) => payment.paymentMethodInfo?.method === 'giftCard'
  );
};

export const getGiftCardAmount = (cart: Cart) => {
  const payments = cart?.payments || [];
  const interfaceInteraction = payments.find(
    (payment) => payment.interfaceInteractions
  );

  const giftCardDetails = interfaceInteraction?.interfaceInteractions?.find(
    (interaction) => interaction.giftCardDetails
  )?.giftCardDetails;
  const amount = giftCardDetails?.details?.find((detail) => detail);
  return amount?.amount;
};

export const isNeedAnotherPaymentMethod = (cart: Cart) => {
  const payments = cart?.payments || [];
  const giftCardPayment = getGiftCardPayent(cart);
  const giftCardAmount = giftCardPayment?.amountPlanned?.centAmount || 0;
  const transactionAmount =
    payments?.[0]?.transactions?.[0]?.amount?.centAmount || 0;
  const needAnotherPaymentMethod = giftCardAmount - transactionAmount > 0;
  return needAnotherPaymentMethod;
};

interface IAddGiftCard extends GiftCardFormFields {
  sendPaymentGiftCardErrorGtm?: (error: IErrorHandlerBilling) => void;
  addGiftCardGtm?: (cardNumber: string) => void;
}
const addGiftCard = createAsyncThunk(
  ADD_GIFTCARD,
  async (payload: IAddGiftCard, { dispatch, rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const { sendPaymentGiftCardErrorGtm, addGiftCardGtm, ...body } = payload;
    try {
      const cart = await postValidateGiftCard(body);
      const transformedCart = httpCartDtoToCart(cart.data);
      const { data: cartPayments } = state.cartPayments;
      const othersPaymentTotalAmount = cartPayments?.otherMethod?.centAmount;
      const payments = transformedCart?.payments;
      const isPaidTotalWithGiftCard = getGiftCardCanPayTotalAmount(
        payments,
        othersPaymentTotalAmount
      );
      if (isPaidTotalWithGiftCard) {
        toast({
          title: '¡Listo! Agregaste tu Gift Card con éxito',
          body: 'Tu saldo es suficiente para continuar con la compra.',
          position: 'top-right',
          status: 'success'
        });
      }
      addGiftCardGtm?.(body.cardNumber);
      return transformedCart;
    } catch (error: unknown) {
      const errorInfo = (error as AxiosError)?.response?.data as IErrorResponse;
      const billingError = errorHandlerBilling(errorInfo);
      dispatch(setGiftCardError(billingError));
      sendPaymentGiftCardErrorGtm?.(billingError);
      return rejectWithValue(errorInfo);
    }
  }
);

const removeGiftCard = createAsyncThunk(REMOVE_GIFTCARD, async () => {
  const cart = await deleteGiftCard();
  return httpCartDtoToCart(cart.data);
});

export { addGiftCard, removeGiftCard };
